import { CampaignDeliveryReport } from './campaignDeliveryReport.js';
import { CampaignJourneyReport } from './campaignJourneyReport.js';
import { ClientList } from './clientList.js';
import { ReportCache } from './reportCache.js';
import type { ApiClient } from './runtime.js';
import { SegmentReport, type SuppressedSegmentMap } from './segmentReport.js';
import type { RunnableReport } from './baseReport.js';
import type { Client, ReportDom, ReportType } from './types.js';
import { UrlState } from './urlState.js';

type AppDom = {
  filterInput: HTMLInputElement;
  selectAllBtn: HTMLElement;
  selectWeekendBtn: HTMLElement;
  deselectAllBtn: HTMLElement;
  clientListEl: HTMLElement;
  selectedCountEl: HTMLElement;
  segmentBtn: HTMLButtonElement;
  campaignJourneyBtn: HTMLButtonElement;
  campaignDeliveryBtn: HTMLButtonElement;
  hoursInput: HTMLInputElement;
  loadingEl: HTMLElement;
  resultsPanel: HTMLElement;
  resultsTitle: HTMLElement;
  resultsContainer: HTMLElement;
  exportCsvBtn: HTMLElement;
  copySummaryBtn: HTMLElement;
  showSummaryBtn: HTMLElement;
  clearCacheBtn: HTMLElement;
};

export class App {
  private readonly dom: AppDom;
  private readonly apiClient: ApiClient;
  private readonly cache = new ReportCache();
  private readonly urlState = new UrlState();
  private readonly suppressedSegmentMap: SuppressedSegmentMap = new Map();
  private readonly clientList: ClientList;
  private readonly segmentReport: SegmentReport;
  private readonly campaignJourneyReport: CampaignJourneyReport;
  private readonly campaignDeliveryReport: CampaignDeliveryReport;
  private activeReport: RunnableReport | null = null;
  private isRunning = false;

  constructor(root: ParentNode, apiClient: ApiClient) {
    this.dom = readDom(root);
    this.apiClient = apiClient;

    this.clientList = new ClientList(
      {
        filterInput: this.dom.filterInput,
        selectAllBtn: this.dom.selectAllBtn,
        selectWeekendBtn: this.dom.selectWeekendBtn,
        deselectAllBtn: this.dom.deselectAllBtn,
        listEl: this.dom.clientListEl,
        countEl: this.dom.selectedCountEl,
      },
      () => this.syncToUrl(),
    );

    const reportDom: ReportDom = {
      panel: this.dom.resultsPanel,
      title: this.dom.resultsTitle,
      container: this.dom.resultsContainer,
      copyBtn: this.dom.copySummaryBtn,
      showBtn: this.dom.showSummaryBtn,
      exportBtn: this.dom.exportCsvBtn,
    };
    const runDeps = {
      setLoading: (on: boolean) => this.setLoading(on),
      onComplete: () => this.syncToUrl(),
    };

    this.segmentReport = new SegmentReport(
      this.cache,
      this.apiClient,
      reportDom,
      runDeps,
      this.suppressedSegmentMap,
    );
    this.campaignJourneyReport = new CampaignJourneyReport(
      this.cache,
      this.apiClient,
      reportDom,
      runDeps,
    );
    this.campaignDeliveryReport = new CampaignDeliveryReport(
      this.cache,
      this.apiClient,
      reportDom,
      runDeps,
    );

    this.bindEvents();
  }

  async init(): Promise<void> {
    await this.loadClients();

    const snapshot = this.urlState.read();
    this.clientList.setSelectedSlugs(snapshot.slugs);
    if (snapshot.hours != null) this.dom.hoursInput.value = String(snapshot.hours);

    this.clientList.render();
    this.clientList.updateCount();

    if (snapshot.report === 'segments' && this.clientList.hasSelection()) {
      void this.runSegmentReport();
    } else if (snapshot.report === 'campaign-journey' && this.clientList.hasSelection()) {
      void this.runCampaignJourneyReport();
    } else if (snapshot.report === 'campaign-delivery' && this.clientList.hasSelection()) {
      void this.runCampaignDeliveryReport();
    }
  }

  private async loadClients(): Promise<void> {
    const clients = (await this.apiClient.getClients()) as Client[];
    this.suppressedSegmentMap.clear();
    for (const c of clients) {
      const ids = (c.segments_to_suppress ?? []).map((s) => s.id);
      this.suppressedSegmentMap.set(c.client_slug, new Set(ids));
    }
    this.clientList.setClients(clients);
  }

  private bindEvents(): void {
    this.dom.segmentBtn.addEventListener('click', () => this.runSegmentReport());
    this.dom.campaignJourneyBtn.addEventListener('click', () =>
      this.runCampaignJourneyReport(),
    );
    this.dom.campaignDeliveryBtn.addEventListener('click', () =>
      this.runCampaignDeliveryReport(),
    );
    this.dom.exportCsvBtn.addEventListener('click', () => this.activeReport?.exportCsv());
    this.dom.copySummaryBtn.addEventListener('click', () =>
      this.activeReport?.copyClientSummary(),
    );
    this.dom.showSummaryBtn.addEventListener('click', () =>
      this.activeReport?.showClientSummary(),
    );
    this.dom.clearCacheBtn.addEventListener('click', () => this.clearAllCaches());
  }

  private async runSegmentReport(): Promise<void> {
    if (this.isRunning) return;
    this.isRunning = true;
    try {
      this.activeReport = this.segmentReport;
      await this.segmentReport.run(this.clientList.getSelectedSlugs());
    } finally {
      this.isRunning = false;
    }
  }

  private async runCampaignJourneyReport(): Promise<void> {
    if (this.isRunning) return;
    this.isRunning = true;
    try {
      const hours = this.readHours();
      this.campaignJourneyReport.setHours(hours);
      this.activeReport = this.campaignJourneyReport;
      await this.campaignJourneyReport.run(this.clientList.getSelectedSlugs());
    } finally {
      this.isRunning = false;
    }
  }

  private async runCampaignDeliveryReport(): Promise<void> {
    if (this.isRunning) return;
    this.isRunning = true;
    try {
      const hours = this.readHours();
      this.campaignDeliveryReport.setHours(hours);
      this.activeReport = this.campaignDeliveryReport;
      await this.campaignDeliveryReport.run(this.clientList.getSelectedSlugs());
    } finally {
      this.isRunning = false;
    }
  }

  private readHours(): number {
    const n = parseInt(this.dom.hoursInput.value, 10);
    if (!Number.isFinite(n) || n <= 0 || n > 720) return 4;
    return n;
  }

  private setLoading(on: boolean): void {
    this.dom.loadingEl.classList.toggle('hidden', !on);
    this.dom.segmentBtn.disabled = on;
    this.dom.campaignJourneyBtn.disabled = on;
    this.dom.campaignDeliveryBtn.disabled = on;
  }

  private syncToUrl(): void {
    const hours = this.readHours();
    const report: ReportType | null = this.activeReport?.reportType ?? null;
    this.urlState.write({
      slugs: this.clientList.getSelectedSlugs(),
      hours,
      report,
    });
  }

  private async clearAllCaches(): Promise<void> {
    this.cache.clear();
    this.segmentReport.clearState();
    this.campaignJourneyReport.clearState();
    this.campaignDeliveryReport.clearState();
    this.activeReport = null;
    this.dom.resultsPanel.classList.add('hidden');
    this.dom.copySummaryBtn.classList.add('hidden');
    this.dom.showSummaryBtn.classList.add('hidden');
    this.dom.exportCsvBtn.classList.add('hidden');
    console.debug('[report-cache] Cleared');

    try {
      const body = await this.apiClient.clearServerCache();
      console.debug(`[api-cache] Server cleared ${body.cleared ?? 0} entries`);
    } catch (err) {
      console.error('[api-cache] Failed to clear server cache:', err);
    }

    this.syncToUrl();
  }
}

function readDom(root: ParentNode): AppDom {
  const byId = <T extends HTMLElement>(id: string): T => {
    const el = root.querySelector<HTMLElement>(`#${id}`);
    if (!el) throw new Error(`Missing element: #${id}`);
    return el as T;
  };

  return {
    filterInput: byId<HTMLInputElement>('filter-input'),
    selectAllBtn: byId('select-all-btn'),
    selectWeekendBtn: byId('select-weekend-btn'),
    deselectAllBtn: byId('deselect-all-btn'),
    clientListEl: byId('client-list'),
    selectedCountEl: byId('selected-count'),
    segmentBtn: byId<HTMLButtonElement>('segment-btn'),
    campaignJourneyBtn: byId<HTMLButtonElement>('campaign-journey-btn'),
    campaignDeliveryBtn: byId<HTMLButtonElement>('campaign-delivery-btn'),
    hoursInput: byId<HTMLInputElement>('hours-input'),
    loadingEl: byId('loading'),
    resultsPanel: byId('results-panel'),
    resultsTitle: byId('results-title'),
    resultsContainer: byId('results-container'),
    exportCsvBtn: byId('export-csv-btn'),
    copySummaryBtn: byId('copy-summary-btn'),
    showSummaryBtn: byId('show-summary-btn'),
    clearCacheBtn: byId('clear-cache-btn'),
  };
}
